/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import IconView from '@airasia-phoenix/react-native/components/IconView';
import BottomSheetView, {
  BottomSheetRefs,
} from '@airasia-phoenix/react-native/components/BottomSheetView';
import SearchableListPageView from '@airasia-phoenix/react-native/components/SearchableListPageView';
import { Alignment } from '@airasia-phoenix/react-native/components/SearchableListPageView/ListEmptyComponent';
import SelectionFieldView, {
  SelectionFieldRefs,
} from '@airasia-phoenix/react-native/components/SelectionFieldView';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import {
  autoCapitalizeValues,
  TextFieldRefs,
} from '@airasia-phoenix/react-native/components/TextFieldView/types';
import {
  getColor,
  getTextStyle,
  pd,
} from '@airasia-phoenix/react-native/utils/utils';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, TouchableOpacity, View, Platform } from 'react-native';
import ERROR_FIELD from '../../error';
import { StationDataObject } from '../../types/FlightSearch.types';
import AsyncStorageOriginDestinationItem from '../../types/recentSearchesAsyncStorage.types';
import Constants from './constants';
import {
  FlightsMemoisedListViewProps,
  FlightsSectionListItem,
  OriginDestinationSelectorProps,
  QueryType,
} from './types';
import {
  flattenRecentSearches,
  getDestinationsForOrigin,
  getNearestAirportsStationsSearchableList,
  getRecentDestinationsSearchableList,
  getRecentSearchesSearchableList,
  getStationDisplayName,
  getStationsSearchableList,
  isEmptyObject,
} from './utils';

import { useLogger } from '@airasia-rn/core-logger';

//analytics
import { FlightSearchEvent, FlightSearchMeta } from '../../analytics';

// hooks
import { TextView } from '@airasia-phoenix/react-native/components';
import { useAppConfig } from '@airasia-rn/ui';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';
import { StyleSheet } from 'react-native-unistyles';
import { useNearestAirportsCodes } from '../../hooks/useNearestAirportsCodes';
import { useNearestAirportsStations } from '../../hooks/useNearestAirportsStations';
import { useRecentSearches } from '../../hooks/useRecentSearches';
import { useStationSearch } from '../../hooks/useStationSearch';
import {
  isSameOriginAndDestination,
  isEitherStationEmpty,
} from '../../util/FlightSearch.utils';
import { useDeviceInfo } from '@airasia-rn/ui';
import { getSelectionFieldViewType } from '../../util/selectionFieldViewType';
import { usePortalUsageEnabled } from '../../hooks';

const ORIGIN_DESTINATION_SELECTOR_ROOT_ID = 'origin-destination-selector-root';

const isScrollableElement = (element: HTMLElement) => {
  const overflowY = window.getComputedStyle(element).overflowY;

  return (
    (overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'overlay') &&
    element.scrollHeight > element.clientHeight
  );
};

const OriginDestinationSelectorView = (
  props: OriginDestinationSelectorProps,
) => {
  const {
    originObj,
    updateOriginObj,
    destinationObj,
    updateDestinationObj,
    originErrorField,
    updateOriginErrorField,
    destinationErrorField,
    updateDestinationErrorField,
    disableOrigin,
    disableArrival,
    screenName,
  } = props;

  const DROP_DOWN_HEIGHT = 389;
  const DROP_DOWN_WIDTH = 360;
  const DROP_DOWN_PORTAL_HOST_NAME = 'flights-web-portal-host';

  const appConfig = useAppConfig();
  const { logEvent } = useLogger();
  const [originList, setOriginList] = useState<
    FlightsMemoisedListViewProps[] | FlightsSectionListItem[]
  >([]);
  const [destinationList, setDestinationList] = useState<
    FlightsMemoisedListViewProps[] | FlightsSectionListItem[]
  >([]);
  const originSelectionFieldRef = useRef<SelectionFieldRefs>(null);
  const destinationSelectionFieldRef = useRef<SelectionFieldRefs>(null);
  const originBottomSheetRef = useRef<BottomSheetRefs>(null);
  const destinationBottomSheetRef = useRef<BottomSheetRefs>(null);
  const originMobileInputRef = useRef<TextFieldRefs>(null);
  const destinationMobileInputRef = useRef<TextFieldRefs>(null);
  const pendingMobileWebFocusRef = useRef<'origin' | 'destination' | null>(
    null,
  );
  const focusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [stationSearchQueryParams, setStationSearchQueryParams] =
    useState<QueryType | null>(null);
  const [isOriginAndDestinationSame, setOriginAndDestination] = useState(false);
  const [originSearchQuery, setOriginSearchQuery] = useState('');
  const [destinationSearchQuery, setDestinationSearchQuery] = useState('');
  const [isOriginTyping, setIsOriginTyping] = useState(false);
  const [isDestinationTyping, setIsDestinationTyping] = useState(false);
  const [activeMobileWebSheet, setActiveMobileWebSheet] = useState<
    'origin' | 'destination' | null
  >(null);
  const hasDestinationFetchedOnce = useRef(false);
  const { isWeb, isMobileWidth } = useDeviceInfo();
  const isMobileWebView = isWeb && isMobileWidth;
  const initialHeight = '80%';
  const finalHeight = '80%';
  const defaultHeight = '100%';

  const {
    data: stationData,
    isLoading: isStationsSearchLoading,
    isError: isStationSearchError,
  } = useStationSearch(stationSearchQueryParams ?? null, screenName);

  const { data: nearestAirportsCodes } = useNearestAirportsCodes(screenName);

  const {
    data: nearestAirportsStations,
    isLoading: nearestAirportsStationsLoading,
    isError: nearestAirportsStationsIsError,
  } = useNearestAirportsStations(screenName);

  const {
    data: recentSearchesStore,
    isLoading: recentSearchesIsLoading,
    isFetched: isRecentSearchesFetched,
  } = useRecentSearches();

  const { t } = useTranslation('flights');

  // Hydration-safe: getCurrentBreakpoint() reads the live unistyles runtime,
  // which is `md` on the server but the real window size on the first client
  // render, so a narrow desktop window failed hydration. isMobileWidth (<768,
  // the same md boundary) follows the server's device hint until mounted.
  const isDesktopView = isWeb ? !isMobileWidth : getCurrentBreakpoint() > 1;

  const { enabled: isPortalUsageEnabled } = usePortalUsageEnabled();

  const searchContextRef = useRef<{
    text: string;
    isDestination?: boolean;
    originObj?: StationDataObject;
  } | null>(null);

  const translations = useMemo(
    () => ({
      nearestAirportLabel: t(
        'flightsSearchPage.main.pluralNearestAirportsLabel',
      ),
      recentSearchesLabel: t('flightsSearchPage.destination.recentSearch'),
      allAirportsLabel: t('flightsSearchPage.main.allAirport'),
      originErrorMsg: t('flightsSearchPage.origin.originErrorMsg'),
      originLabel: t('flightsSearchPage.origin.originLabel'),
      noResults: t('flightsSearchPage.main.noResults'),
      originNoResults: t('flightsSearchPage.origin.noResults'),
      destinationErrorMsg: t(
        'flightsSearchPage.destination.destinationErrorMsg',
      ),
      destinationLabel: t('flightsSearchPage.destination.destinationLabel'),
      destinationNoResults: t('flightsSearchPage.destination.noResults'),
    }),
    [t],
  );

  useEffect(() => {
    setOriginAndDestination(
      isSameOriginAndDestination(originObj, destinationObj),
    );
  }, [originObj, destinationObj]);

  // run init only once (prevents updates from re-triggering logic)
  const didInitRef = useRef(false);

  const recentSearches = recentSearchesStore?.recentSearches;

  const recentPairs: AsyncStorageOriginDestinationItem[] = useMemo(
    () => flattenRecentSearches(recentSearches),
    [recentSearches],
  );

  const buildOriginDefaultList = useCallback(() => {
    if (recentSearches?.length && recentSearches?.length > 0) {
      return getRecentSearchesSearchableList(
        translations.recentSearchesLabel,
        translations.allAirportsLabel,
        recentSearches,
      );
    }
    return getNearestAirportsStationsSearchableList(
      translations.nearestAirportLabel,
      translations.allAirportsLabel,
      nearestAirportsStations,
    );
  }, [
    recentSearches,
    nearestAirportsStations,
    translations.recentSearchesLabel,
    translations.nearestAirportLabel,
    translations.allAirportsLabel,
  ]);

  const buildDestinationDefaultList = useCallback(
    (origin?: StationDataObject) => {
      const modifiedDestinationRecentSearches = getDestinationsForOrigin(
        origin,
        recentSearches,
      );

      return getRecentDestinationsSearchableList(
        translations.recentSearchesLabel,
        translations.allAirportsLabel,
        modifiedDestinationRecentSearches,
      );
    },
    [
      recentSearches,
      translations.recentSearchesLabel,
      translations.allAirportsLabel,
    ],
  );

  const resetOriginList = useCallback(() => {
    setOriginList(buildOriginDefaultList());
  }, [buildOriginDefaultList]);

  const resetDestinationList = useCallback(
    (origin?: StationDataObject) => {
      setDestinationList(buildDestinationDefaultList(origin));
    },
    [buildDestinationDefaultList],
  );

  useEffect(() => {
    // If we already have values from props (Deep Link), mark as initialized
    // and DO NOT overwrite with recent searches or nearest airports.
    if (
      !didInitRef.current &&
      (!isEmptyObject(originObj) || !isEmptyObject(destinationObj))
    ) {
      didInitRef.current = true;
      // Build lists with current available data
      resetOriginList();
      resetDestinationList(originObj);
      return;
    }

    if (didInitRef.current) return;

    const finishInit = () => {
      didInitRef.current = true;
    };

    if (recentPairs.length > 0) {
      const firstPair = recentPairs[0];
      const firstOrigin = firstPair?.originStationObject;

      if (firstOrigin) {
        updateOriginObj(firstOrigin);
        updateDestinationObj(firstPair.destinationStationObject);
      }

      // origin list should show recent searches
      resetOriginList();

      // destination list should show recent destinations for that origin
      if (firstOrigin) resetDestinationList(firstOrigin);
      else setDestinationList([]);

      finishInit();
      return;
    }

    if (nearestAirportsStationsLoading) return;

    if (nearestAirportsStations?.length > 0) {
      const firstNearest = nearestAirportsStations[0];
      updateOriginObj(firstNearest);
      updateDestinationObj(undefined);

      resetOriginList(); // will fall back to nearest airports list
      setDestinationList([]); // matches your current behavior
      finishInit();
      return;
    }

    if (!isEmptyObject(originObj) || !isEmptyObject(destinationObj)) {
      finishInit();
    }
  }, [
    recentPairs,
    nearestAirportsStationsLoading,
    nearestAirportsStations,
    originObj,
    destinationObj,
    resetOriginList,
    resetDestinationList,
    updateOriginObj,
    updateDestinationObj,
  ]);

  useEffect(() => {
    if (originSearchQuery.trim() || isOriginTyping) return;

    resetOriginList();
  }, [
    recentSearches,
    nearestAirportsStations,
    isOriginTyping,
    originSearchQuery,
  ]);

  useEffect(() => {
    if (destinationSearchQuery.trim() || isDestinationTyping) return;

    if (isEmptyObject(originObj)) {
      setDestinationList([]);
      return;
    }

    resetDestinationList(originObj);
  }, [recentSearches, originObj, isDestinationTyping, destinationSearchQuery]);

  const getSearchData = useCallback(
    async (
      text: string,
      isDestination: boolean = false,
      originObj?: StationDataObject,
    ) => {
      if (isDestination && isEmptyObject(originObj)) return;

      searchContextRef.current = { text, isDestination, originObj };

      const queryParams: QueryType = {
        query: text,
        locale: appConfig.config.locale,
        locKey: nearestAirportsCodes?.locKey,
        ...(isDestination && originObj
          ? {
              origin: originObj.StationCode,
              ...(originObj.IsCity ? { isCity: true } : {}),
            }
          : {}),
      };

      setStationSearchQueryParams(queryParams);
    },
    [appConfig.config.locale, nearestAirportsCodes?.locKey],
  );

  const debouncedGetSearchData = useMemo(() => {
    const fn = debounce(getSearchData, Constants.DEBOUNCE_TIME);
    return fn;
  }, [getSearchData]);

  /**
   * Station search response effect
   */
  useEffect(() => {
    if (!stationSearchQueryParams || !searchContextRef.current) return;

    const { isDestination, text } = searchContextRef.current;

    if (text.length === 0) {
      resetOriginList();
      if (isDestination) resetDestinationList(originObj);
      else setDestinationList([]);

      setStationSearchQueryParams(null);
      return;
    }

    if (isStationSearchError) {
      setStationSearchQueryParams(null);
      return;
    }

    if (isStationsSearchLoading) return;
    if (!stationData) return;

    if (stationData.length === 0) {
      isDestination ? setDestinationList([]) : setOriginList([]);
    } else {
      const modified = getStationsSearchableList(
        stationData,
        translations.allAirportsLabel,
        text,
      );

      isDestination ? setDestinationList(modified) : setOriginList(modified);
      if (isDestination) {
        if (!hasDestinationFetchedOnce.current) {
          hasDestinationFetchedOnce.current = true;
        }
      }
    }
    setIsOriginTyping(false);
    setIsDestinationTyping(false);
    setStationSearchQueryParams(null);
  }, [
    stationSearchQueryParams,
    stationData,
    isStationSearchError,
    isStationsSearchLoading,
    originObj,
    translations.allAirportsLabel,
    resetOriginList,
    resetDestinationList,
  ]);

  useEffect(() => {
    return () => {
      debouncedGetSearchData.cancel?.();
    };
  }, [debouncedGetSearchData]);

  const sendStationSelectionEvent = (
    stationObj: StationDataObject,
    pickerType: string,
  ) => {
    if (!stationObj) return;

    const { ANALYTICS_CONSTANTS, EVENT_NAMES } = FlightSearchMeta;

    const isCity = stationObj.IsCity ?? false;
    const stationOrCityCode = isCity
      ? `${stationObj?.CityCode}|${ANALYTICS_CONSTANTS.CITY}`
      : `${stationObj?.StationCode}|${ANALYTICS_CONSTANTS.AIRPORT}`;
    const params = {
      category: `${ANALYTICS_CONSTANTS.FLIGHTS}|${pickerType}|${ANALYTICS_CONSTANTS.FLIGHT_SEARCH_WIDGET}`,
      label: `${stationOrCityCode}|${
        stationObj?.AAFlight ? ANALYTICS_CONSTANTS.AA : ANALYTICS_CONSTANTS.OTA
      }`,
    };
    logEvent(
      EVENT_NAMES.click,
      FlightSearchEvent.stationSelectionEvent(params),
    );
  };

  const closeOriginMobileWebSheet = useCallback(() => {
    setActiveMobileWebSheet(null);
    pendingMobileWebFocusRef.current = null;
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
    setIsMobileWebDatePickerBottomSheetOpen(false);
    setOriginSearchQuery('');
    setIsOriginTyping(false);
    resetOriginList();
    Keyboard.dismiss();
  }, [resetOriginList]);

  const closeDestinationMobileWebSheet = useCallback(() => {
    setActiveMobileWebSheet(null);
    pendingMobileWebFocusRef.current = null;
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
    setIsMobileWebDatePickerBottomSheetOpen(false);
    setDestinationSearchQuery('');
    setIsDestinationTyping(false);
    resetDestinationList(originObj);
    Keyboard.dismiss();
  }, [originObj, resetDestinationList]);

  const [
    isMobileWebDatePickerBottomSheetOpen,
    setIsMobileWebDatePickerBottomSheetOpen,
  ] = useState(false);

  const openMobileWebSheet = useCallback(
    (picker: 'origin' | 'destination') => {
      if (!isMobileWebView) return;

      pendingMobileWebFocusRef.current = picker;
      setActiveMobileWebSheet(picker);
      setIsMobileWebDatePickerBottomSheetOpen(true);
      requestAnimationFrame(() => {
        if (picker === 'origin') {
          originBottomSheetRef.current?.toggleBottomSheetView();
          return;
        }

        destinationBottomSheetRef.current?.toggleBottomSheetView();
      });
    },
    [isMobileWebView, isMobileWebDatePickerBottomSheetOpen],
  );

  useEffect(() => {
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isMobileWebView || !activeMobileWebSheet) return;
    if (Platform.OS !== 'web' || typeof document === 'undefined') return;

    const lockedElements: Array<{
      element: HTMLElement;
      overflow: string;
      overscrollBehavior: string;
      touchAction: string;
    }> = [];

    const lockElement = (element: HTMLElement | null) => {
      if (!element) return;
      if (lockedElements.some((item) => item.element === element)) return;

      lockedElements.push({
        element,
        overflow: element.style.overflow,
        overscrollBehavior: (element.style as CSSStyleDeclaration)
          .overscrollBehavior,
        touchAction: element.style.touchAction,
      });

      element.style.overflow = 'hidden';
      (element.style as CSSStyleDeclaration).overscrollBehavior = 'none';
      element.style.touchAction = 'none';
    };

    const root =
      document.getElementById(ORIGIN_DESTINATION_SELECTOR_ROOT_ID) ||
      (document.querySelector(
        `[data-nativeid="${ORIGIN_DESTINATION_SELECTOR_ROOT_ID}"]`,
      ) as HTMLElement | null);

    let currentElement = root?.parentElement ?? null;
    while (currentElement) {
      if (isScrollableElement(currentElement)) {
        lockElement(currentElement);
      }
      currentElement = currentElement.parentElement;
    }

    lockElement(document.body);
    lockElement(document.documentElement);

    return () => {
      lockedElements.forEach(
        ({ element, overflow, overscrollBehavior, touchAction }) => {
          element.style.overflow = overflow;
          (element.style as CSSStyleDeclaration).overscrollBehavior =
            overscrollBehavior;
          element.style.touchAction = touchAction;
        },
      );
    };
  }, [activeMobileWebSheet, isMobileWebView]);

  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    if (!isMobileWebDatePickerBottomSheetOpen) return;

    if (typeof document === 'undefined' || !window.visualViewport) return;

    const viewport = window.visualViewport;

    const update = () => {
      setViewportHeight(viewport.height);
    };

    update();

    viewport.addEventListener('resize', update);

    return () => {
      viewport.removeEventListener('resize', update);
    };
  }, [isMobileWebDatePickerBottomSheetOpen]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const root = document.getElementById(
      ORIGIN_DESTINATION_SELECTOR_ROOT_ID,
    )?.parentElement;

    if (!root) return;

    const originalOverflow = root.style.overflow;
    const originalOverscroll = root.style.overscrollBehavior;

    // disable outer scroll
    root.style.overflow = 'visible';
    root.style.overscrollBehavior = 'none';

    return () => {
      // restore
      root.style.overflow = originalOverflow;
      root.style.overscrollBehavior = originalOverscroll;
    };
  }, []);

  return (
    <View nativeID={ORIGIN_DESTINATION_SELECTOR_ROOT_ID}>
      <View style={styles.container}>
        {/* Origin Block */}

        <View
          style={styles.originContainer}
          pointerEvents={disableOrigin ? 'none' : 'auto'}
        >
          {isMobileWebView ? (
            <>
              <TextFieldView
                disabled={!!disableOrigin}
                label={translations.originLabel}
                value={getStationDisplayName(
                  originObj,
                  translations.allAirportsLabel,
                )}
                isError={originErrorField === ERROR_FIELD.ORIGIN_FIELD}
                errorMessage={translations.originErrorMsg}
                editable={false}
                onPress={
                  disableOrigin ? undefined : () => openMobileWebSheet('origin')
                }
                startIcon={{
                  icon: pd.icon.main.communication.location_on,
                  height: 24,
                  width: 24,
                  tint: getColor(pd.sys.colour.content_secondary),
                }}
              />
              <BottomSheetView
                ref={originBottomSheetRef}
                enableContentPanningGesture={false}
                enablePanDownToClose={false}
                enableOverDrag={false}
                showBackdrop
                showHandle={false}
                showHeader={false}
                sectionTitle=""
                initialHeight="100%"
                finalHeight="100%"
                height={viewportHeight}
                backdropProps={{
                  opacity: 1,
                  backgroundColor: getColor(pd.sys.colour.overlay),
                }}
                onChange={(index) => {
                  if (
                    index >= 0 &&
                    pendingMobileWebFocusRef.current === 'origin'
                  ) {
                    pendingMobileWebFocusRef.current = null;
                    if (focusTimeoutRef.current) {
                      clearTimeout(focusTimeoutRef.current);
                    }
                    focusTimeoutRef.current = setTimeout(() => {
                      originMobileInputRef.current?.focus();
                    }, 150);
                  }
                }}
                onBottomSheetClose={closeOriginMobileWebSheet}
                disableBorderRadius={!isDesktopView ? true : false}
              >
                <View style={styles.sheetHeaderRow}>
                  <TouchableOpacity
                    style={styles.sheetCloseButton}
                    onPress={() => {
                      originBottomSheetRef.current?.toggleBottomSheetView();
                    }}
                  >
                    <IconView
                      icon={pd.icon.main.navigation.close}
                      height={24}
                      width={24}
                      tint={getColor(pd.sys.colour.content_primary)}
                    />
                  </TouchableOpacity>
                  <View style={styles.sheetInputWrapper}>
                    <TextFieldView
                      ref={originMobileInputRef}
                      disabled={false}
                      label={translations.originLabel}
                      value={originSearchQuery}
                      autoCorrect={false}
                      autoCapitalize={autoCapitalizeValues.NONE}
                      onChangeText={(text) => {
                        setOriginSearchQuery(text);
                        if (!text.trim()) {
                          debouncedGetSearchData.cancel();
                          setIsOriginTyping(false);
                          resetOriginList();
                          return;
                        }
                        setIsOriginTyping(true);
                        debouncedGetSearchData(text);
                      }}
                      startIcon={{
                        icon: pd.icon.main.communication.location_on,
                        height: 24,
                        width: 24,
                        tint: getColor(pd.sys.colour.content_secondary),
                      }}
                    />
                  </View>
                </View>
                <View style={styles.sheetListContainer}>
                  <SearchableListPageView
                    searchableListPageViewData={originList}
                    startedSearching={
                      !isOriginTyping &&
                      !isStationsSearchLoading &&
                      originSearchQuery.length > 0 &&
                      originList.length === 0
                    }
                    listEmptyComponentProps={{
                      contentStateViewProps: {
                        primaryText: translations.noResults,
                        secondaryText: translations.originNoResults,
                      },
                      alignment: Alignment.TOP,
                    }}
                    // @ts-expect-error some type issue
                    headerTextStyle={getTextStyle(
                      pd.sys.typescale.sub_body_text,
                      pd.sys.colour.content_secondary,
                    )}
                    headerViewStyle={{ paddingVertical: 4 }}
                    onChange={(selectedItem) => {
                      Keyboard.dismiss();
                      if (selectedItem) {
                        const selectedObj = (
                          selectedItem[0] as FlightsMemoisedListViewProps
                        ).model.value;
                        updateOriginObj(selectedObj);
                        sendStationSelectionEvent(selectedObj, 'origin');
                        resetDestinationList(selectedObj);
                      } else {
                        updateOriginObj(undefined);
                        resetDestinationList(undefined);
                      }
                      if (!disableArrival) {
                        updateDestinationObj(undefined);
                      }
                      updateOriginErrorField?.(undefined);
                      originBottomSheetRef.current?.toggleBottomSheetView();
                    }}
                    numColumns={1}
                    isInsideBottomSheetView={false}
                    isTextFieldVisible={false}
                  />
                </View>
              </BottomSheetView>
            </>
          ) : (
            <SelectionFieldView
              disabled={!!disableOrigin}
              isError={originErrorField === ERROR_FIELD.ORIGIN_FIELD}
              errorMessage={translations.originErrorMsg}
              ref={originSelectionFieldRef}
              startIcon={{
                icon: pd.icon.main.communication.location_on,
                height: 24,
                width: 24,
                tint: getColor(pd.sys.colour.content_secondary),
              }}
              endIcon={undefined}
              label={translations.originLabel}
              searchableListViewProps={{
                paddingTop: isPortalUsageEnabled ? undefined : 54,
                startedSearching:
                  !isOriginTyping &&
                  !isStationsSearchLoading &&
                  originSearchQuery.length > 0 &&
                  originList.length === 0,
                viewType: getSelectionFieldViewType(
                  isPortalUsageEnabled,
                  isDesktopView,
                ),
                dropDownHeight: DROP_DOWN_HEIGHT,
                dropDownWidth: DROP_DOWN_WIDTH,
                dropdownPortalHostName: DROP_DOWN_PORTAL_HOST_NAME,
                isTextFieldVisible: isDesktopView ? false : true,
                dropDownZIndex: 23,
                //@ts-expect-error some type issue
                headerTextStyle: getTextStyle(
                  pd.sys.typescale.sub_body_text,
                  pd.sys.colour.content_secondary,
                ),
                headerViewStyle: { paddingVertical: 4 },
                listEmptyComponentProps: {
                  contentStateViewProps: {
                    primaryText: translations.noResults,
                    secondaryText: translations.originNoResults,
                  },
                  alignment: Alignment.TOP,
                },
                textFieldViewProps: {
                  autoFocus: true,
                  selectTextOnFocus: isDesktopView ? true : false,
                  autoCorrect: false,
                  autoCapitalize: autoCapitalizeValues.NONE,
                  label: translations.originLabel,
                  disabled: !!disableOrigin,
                  endIcon: {
                    icon: pd.icon.main.navigation.cancel,
                    height: 24,
                    width: 24,
                    tint: getColor(pd.sys.colour.content_primary),
                  },
                  onEndIconPress: () => {
                    resetOriginList();
                  },
                },
                searchableListPageViewData: originList,
                onChange: (selectedItem) => {
                  Keyboard.dismiss();
                  if (selectedItem) {
                    const selectedObj = (
                      selectedItem[0] as FlightsMemoisedListViewProps
                    ).model.value;
                    updateOriginObj(selectedObj);
                    sendStationSelectionEvent(selectedObj, 'origin');
                    resetDestinationList(selectedObj);
                  } else {
                    updateOriginObj(undefined);
                    resetDestinationList(undefined);
                  }
                  if (!disableArrival) {
                    updateDestinationObj(undefined);
                  }

                  resetOriginList();

                  if (!isDesktopView) {
                    originSelectionFieldRef.current?.toggleBottomSheetView();
                    originSelectionFieldRef.current?.blurWithText();
                  }
                  updateOriginErrorField?.(undefined);
                  setOriginSearchQuery('');
                },
                onChangeText: (text) => {
                  setOriginSearchQuery(text);
                  if (!text.trim()) {
                    debouncedGetSearchData.cancel();
                    setIsOriginTyping(false);
                    resetOriginList();
                    return;
                  }
                  setIsOriginTyping(true);
                  debouncedGetSearchData(text);
                },
              }}
              value={getStationDisplayName(
                originObj,
                translations.allAirportsLabel,
              )}
              bottomSheetViewProps={{
                initialHeight: initialHeight,
                finalHeight: finalHeight,
                height: defaultHeight,
                children: undefined,
                showHandle: false,
                enableDynamicSize: true,
                onBottomSheetClose: () => {
                  Keyboard.dismiss();
                  originSelectionFieldRef.current?.blurWithText();
                  originSelectionFieldRef.current?.blur();
                  resetOriginList();
                },
                sectionTitle: '',
                onCloseIconPress: () => {
                  Keyboard.dismiss();
                  originSelectionFieldRef.current?.blurWithText();
                  originSelectionFieldRef.current?.blur();
                  resetOriginList();
                },
              }}
            />
          )}
        </View>

        {/* Destination Block */}

        <View
          style={styles.destinationContainer}
          pointerEvents={disableArrival ? 'none' : 'auto'}
        >
          {isMobileWebView ? (
            <>
              <TextFieldView
                disabled={!!disableArrival}
                label={translations.destinationLabel}
                value={getStationDisplayName(
                  destinationObj,
                  translations.allAirportsLabel,
                )}
                isError={
                  destinationErrorField === ERROR_FIELD.DESTINATION_FIELD
                }
                errorMessage={translations.destinationErrorMsg}
                editable={false}
                onPress={
                  disableArrival
                    ? undefined
                    : () => openMobileWebSheet('destination')
                }
                startIcon={{
                  icon: pd.icon.main.communication.location_on,
                  height: 24,
                  width: 24,
                  tint: getColor(pd.sys.colour.content_secondary),
                }}
              />
              <BottomSheetView
                ref={destinationBottomSheetRef}
                enableContentPanningGesture={false}
                enablePanDownToClose={false}
                enableOverDrag={false}
                showBackdrop
                showHandle={false}
                showHeader={false}
                sectionTitle=""
                initialHeight="100%"
                finalHeight="100%"
                height={viewportHeight}
                backdropProps={{
                  opacity: 1,
                  backgroundColor: getColor(pd.sys.colour.overlay),
                }}
                onChange={(index) => {
                  if (
                    index >= 0 &&
                    pendingMobileWebFocusRef.current === 'destination'
                  ) {
                    pendingMobileWebFocusRef.current = null;
                    if (focusTimeoutRef.current) {
                      clearTimeout(focusTimeoutRef.current);
                    }
                    focusTimeoutRef.current = setTimeout(() => {
                      destinationMobileInputRef.current?.focus();
                    }, 150);
                  }
                }}
                onBottomSheetClose={closeDestinationMobileWebSheet}
                disableBorderRadius={!isDesktopView ? true : false}
              >
                <View style={styles.sheetHeaderRow}>
                  <TouchableOpacity
                    style={styles.sheetCloseButton}
                    onPress={() => {
                      destinationBottomSheetRef.current?.toggleBottomSheetView();
                    }}
                  >
                    <IconView
                      icon={pd.icon.main.navigation.close}
                      height={24}
                      width={24}
                      tint={getColor(pd.sys.colour.content_primary)}
                    />
                  </TouchableOpacity>
                  <View style={styles.sheetInputWrapper}>
                    <TextFieldView
                      ref={destinationMobileInputRef}
                      disabled={false}
                      label={translations.destinationLabel}
                      value={destinationSearchQuery}
                      autoCorrect={false}
                      autoCapitalize={autoCapitalizeValues.NONE}
                      onChangeText={(text) => {
                        setDestinationSearchQuery(text);
                        if (!text.trim()) {
                          debouncedGetSearchData.cancel();
                          setIsDestinationTyping(false);
                          resetDestinationList(originObj);
                          return;
                        }
                        setIsDestinationTyping(true);
                        debouncedGetSearchData(text, true, originObj);
                      }}
                      startIcon={{
                        icon: pd.icon.main.communication.location_on,
                        height: 24,
                        width: 24,
                        tint: getColor(pd.sys.colour.content_secondary),
                      }}
                    />
                  </View>
                </View>
                <View style={styles.sheetListContainer}>
                  <SearchableListPageView
                    searchableListPageViewData={destinationList}
                    isLoading={
                      !hasDestinationFetchedOnce.current &&
                      isStationsSearchLoading
                    }
                    startedSearching={
                      !isDestinationTyping &&
                      !isStationsSearchLoading &&
                      destinationSearchQuery.length > 0 &&
                      destinationList.length === 0
                    }
                    listEmptyComponentProps={{
                      contentStateViewProps: {
                        primaryText: translations.noResults,
                        secondaryText: translations.destinationNoResults,
                      },
                      alignment: Alignment.TOP,
                    }}
                    // @ts-expect-error some type issue
                    headerTextStyle={getTextStyle(
                      pd.sys.typescale.sub_body_text,
                      pd.sys.colour.content_secondary,
                    )}
                    headerViewStyle={{ paddingVertical: 4 }}
                    onChange={(selectedItem) => {
                      Keyboard.dismiss();
                      if (selectedItem) {
                        const selectedObj = (
                          selectedItem[0] as FlightsMemoisedListViewProps
                        ).model.value;
                        sendStationSelectionEvent(selectedObj, 'destination');
                        updateDestinationObj(selectedObj);
                      } else {
                        updateDestinationObj(undefined);
                      }
                      updateDestinationErrorField?.(undefined);
                      destinationBottomSheetRef.current?.toggleBottomSheetView();
                    }}
                    numColumns={1}
                    isInsideBottomSheetView={false}
                    isTextFieldVisible={false}
                  />
                </View>
              </BottomSheetView>
            </>
          ) : (
            <SelectionFieldView
              disabled={!!disableArrival}
              isError={destinationErrorField === ERROR_FIELD.DESTINATION_FIELD}
              errorMessage={translations.destinationErrorMsg}
              // disabled={isEmptyObject(originObj)} // commenting this out as per the UX request
              ref={destinationSelectionFieldRef}
              startIcon={{
                icon: pd.icon.main.communication.location_on,
                height: 24,
                width: 24,
                tint: getColor(pd.sys.colour.content_secondary),
              }}
              endIcon={undefined}
              label={translations.destinationLabel}
              searchableListViewProps={{
                paddingTop: isPortalUsageEnabled ? undefined : 54,
                startedSearching:
                  !isDestinationTyping &&
                  !isStationsSearchLoading &&
                  destinationSearchQuery.length > 0 &&
                  destinationList.length === 0,
                viewType: getSelectionFieldViewType(
                  isPortalUsageEnabled,
                  isDesktopView,
                ),
                dropDownHeight: DROP_DOWN_HEIGHT,
                dropDownWidth: DROP_DOWN_WIDTH,
                dropdownPortalHostName: DROP_DOWN_PORTAL_HOST_NAME,
                isTextFieldVisible: isDesktopView ? false : true,
                dropDownZIndex: 24,
                //@ts-expect-error some type issue
                headerTextStyle: getTextStyle(
                  pd.sys.typescale.sub_body_text,
                  pd.sys.colour.content_secondary,
                ),
                headerViewStyle: { paddingVertical: 4 },
                listEmptyComponentProps: {
                  contentStateViewProps: {
                    primaryText: translations.noResults,
                    secondaryText: translations.destinationNoResults,
                  },
                  alignment: Alignment.TOP,
                },
                textFieldViewProps: {
                  autoFocus: true,
                  selectTextOnFocus: isDesktopView ? true : false,
                  autoCorrect: false,
                  autoCapitalize: autoCapitalizeValues.NONE,
                  label: translations.destinationLabel,
                  disabled: !!disableArrival,
                  endIcon: {
                    icon: pd.icon.main.navigation.cancel,
                    height: 24,
                    width: 24,
                    tint: getColor(pd.sys.colour.content_primary),
                  },
                  onEndIconPress: () => {
                    resetDestinationList(originObj);
                  },
                },
                searchableListPageViewData: destinationList,
                isLoading:
                  !hasDestinationFetchedOnce.current && isStationsSearchLoading,
                onChange: (selectedItem) => {
                  Keyboard.dismiss();
                  if (selectedItem) {
                    const selectedObj = (
                      selectedItem[0] as FlightsMemoisedListViewProps
                    ).model.value;
                    sendStationSelectionEvent(selectedObj, 'destination');
                    updateDestinationObj(selectedObj, true);
                  } else {
                    updateDestinationObj(undefined);
                  }

                  if (!isDesktopView) {
                    destinationSelectionFieldRef.current?.toggleBottomSheetView();
                    destinationSelectionFieldRef.current?.blurWithText();
                  }

                  resetDestinationList(originObj);

                  updateDestinationErrorField?.(undefined);
                  setDestinationSearchQuery('');
                },
                onChangeText: (text) => {
                  setDestinationSearchQuery(text);

                  if (!text.trim()) {
                    debouncedGetSearchData.cancel();
                    setIsDestinationTyping(false);
                    resetDestinationList(originObj);
                    return;
                  }
                  setIsDestinationTyping(true);
                  debouncedGetSearchData(text, true, originObj);
                },
              }}
              value={getStationDisplayName(
                destinationObj,
                translations.allAirportsLabel,
              )}
              bottomSheetViewProps={{
                initialHeight: initialHeight,
                finalHeight: finalHeight,
                height: defaultHeight,
                children: undefined,
                showHandle: false,
                onBottomSheetClose: () => {
                  Keyboard.dismiss();
                  destinationSelectionFieldRef.current?.blurWithText();
                  destinationSelectionFieldRef.current?.blur();
                  resetDestinationList(originObj);
                },
                sectionTitle: '',
                onCloseIconPress: () => {
                  Keyboard.dismiss();
                  destinationSelectionFieldRef.current?.blurWithText();
                  destinationSelectionFieldRef.current?.blur();
                  resetDestinationList(originObj);
                },
              }}
            />
          )}
        </View>

        {/* Swap Overlay */}

        <TouchableOpacity
          activeOpacity={1}
          style={styles.swapOverlay}
          onPress={() => {
            if (
              !originObj ||
              !destinationObj ||
              disableOrigin ||
              disableArrival
            )
              return;

            debouncedGetSearchData.cancel?.();
            setStationSearchQueryParams(null);

            const newOrigin = destinationObj;
            const newDestination = originObj;

            updateOriginObj(newOrigin);
            updateDestinationObj(newDestination);

            resetDestinationList(newOrigin);
            resetOriginList();
          }}
        >
          <View
            style={[
              styles.swapView,
              (isEitherStationEmpty(originObj, destinationObj) ||
                disableOrigin ||
                disableArrival) && {
                backgroundColor: getColor(pd.sys.colour.bg_secondary),
              },
              (originErrorField === ERROR_FIELD.ORIGIN_FIELD ||
                destinationErrorField === ERROR_FIELD.DESTINATION_FIELD) && {
                top: -12,
              },
            ]}
          >
            <IconView
              icon={
                isDesktopView
                  ? pd.icon.main.actions.swap_horiz
                  : pd.icon.main.actions.swap_vert
              }
              height={24}
              width={24}
              tint={
                isEitherStationEmpty(originObj, destinationObj) ||
                disableOrigin ||
                disableArrival
                  ? getColor(pd.sys.colour.outline_low)
                  : getColor(pd.sys.colour.content_secondary)
              }
            />
          </View>
        </TouchableOpacity>
      </View>

      {isOriginAndDestinationSame && (
        <View style={{ paddingTop: 6, paddingHorizontal: 3 }}>
          <TextView
            text={t('flightsSearchPage.main.sameOriginAndDestination')}
            style={getTextStyle(
              pd.sys.typescale.caption_text,
              pd.comp.text_field.error.outline.colour,
            )}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create((pd) => ({
  container: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    justifyContent: 'space-between',
    gap: {
      xs: 12,
      md: 10,
    },
    zIndex: 5,
  },
  originContainer: {
    flex: 1,
    zIndex: 2,
    position: 'relative',
  },
  destinationContainer: {
    flex: 1,
    zIndex: 1,
    position: 'relative',
  },
  sheetHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: pd.ref.space.spacing20.px,
    paddingTop: pd.ref.space.spacing10.px,
    paddingBottom: pd.ref.space.spacing10.px,
    gap: pd.ref.space.spacing10.px,
  },
  sheetCloseButton: {
    paddingTop: pd.ref.space.spacing20.px,
    paddingLeft: pd.ref.space.spacing20.px,
    paddingRight: pd.ref.space.spacing10.px,
  },
  sheetInputWrapper: {
    flex: 1,
    paddingTop: pd.ref.space.spacing40.px,
    paddingLeft: pd.ref.space.spacing10.px,
    paddingRight: pd.ref.space.spacing30.px,
  },
  sheetListContainer: {
    flex: 1,
  },
  swapView: {
    borderColor: getColor(pd.sys.colour.outline_medium),
    backgroundColor: getColor(pd.sys.colour.container_none),
    height: 32,
    width: 32,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 100,
  },
  swapOverlay: {
    position: 'absolute',
    zIndex: 3,
    left: {
      xs: 'auto',
      md: '50%',
    },
    right: {
      xs: 16,
      md: 'auto',
    },
    top: '50%',
    transform: [{ translateX: -16 }, { translateY: -16 }],
  },
}));

export default OriginDestinationSelectorView;
